import { Stack } from '@mui/material';
import { useState } from 'react';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { PresentationTemplateSelectionModal } from '@/modules/presentationTemplates/presentationTemplateSelectionModal/PresentationTemplateSelectionModal';
import { ROUTE_KEYS } from '@/navigation/constants';

import { useEstimatedSetupProgress } from './hooks/useEstimatedSetupProgress';
import { useSetupReviewSuggestions } from './hooks/useSetupReviewSuggestions';
import {
  DeliverablesCardProps,
  PresentationSetupStatus,
  ProfileSetupStatus,
} from './SetupDeliverablesCard';
import { CompletionHero, ProcessingPanel } from './SetupProgressPanels';

export interface HouseholdSetupProgressProps {
  householdId: string;
  householdName: string;
  showCompletion: boolean;
  onDismissCompletion: () => void;
  profileRunning: boolean;
  presentationRunning: boolean;
  presentationCount?: number;
  singlePresentationId?: string;
  onboardingRunning?: boolean;
  onboardingFailed?: boolean;
  processingStartedAt?: Date | string | null;
  hasPresentation: boolean;
  presentationFailed: boolean;
}

function getProfileStatus(
  running: boolean,
  suggestionCount?: number
): ProfileSetupStatus {
  if (suggestionCount !== undefined && suggestionCount > 0) return 'review';
  if (running) return 'processing';
  return suggestionCount === 0 ? 'ready' : 'unknown';
}

function getPresentationStatus(
  running: boolean,
  failed: boolean,
  hasPresentation: boolean
): PresentationSetupStatus {
  if (failed) return 'failed';
  if (running) return 'processing';
  return hasPresentation ? 'ready' : 'not-created';
}

export function HouseholdSetupProgress({
  householdId,
  householdName,
  showCompletion,
  onDismissCompletion,
  profileRunning,
  presentationRunning,
  presentationCount = 1,
  singlePresentationId,
  onboardingRunning,
  onboardingFailed = false,
  processingStartedAt,
  hasPresentation,
  presentationFailed,
}: HouseholdSetupProgressProps) {
  const { navigate } = useNavigateToRoute();
  const canEdit = useCanEditHouseholdData();
  const review = useTrowser(TrowserKey.ReviewSuggestions);
  const trackUserEvent = useTrackUserEvent();
  const [createPresentation, setCreatePresentation] = useState(false);
  // The umbrella workflow can run without any requested presentations.
  const profileWorkRunning =
    profileRunning || (!!onboardingRunning && !presentationRunning);
  const running = profileRunning || presentationRunning || !!onboardingRunning;
  const suggestionCount = useSetupReviewSuggestions({
    householdId,
    enabled: canEdit,
    running: profileWorkRunning,
    reviewOpen: review.isOpen,
  });
  const onReviewSuggestions = () => {
    review.open();
    trackUserEvent('ai_suggestion review_opened', {
      householdId,
      source: 'household_setup',
    });
  };
  const profile = getProfileStatus(profileWorkRunning, suggestionCount);
  const presentation = getPresentationStatus(
    presentationRunning,
    presentationFailed,
    hasPresentation
  );
  // Keep raw tracker activity for timing, even when the same task reports failure.
  const failed = presentation === 'failed' || onboardingFailed;
  const { progress, showProcessing } = useEstimatedSetupProgress({
    running,
    failed,
    startedAt: processingStartedAt,
    scopeKey: householdId,
    clockMode: onboardingRunning ? 'milestone' : 'continuous',
  });
  const complete = !showProcessing && !failed;
  const viewPresentations = () => {
    if (presentationCount === 1 && singlePresentationId) {
      return navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2, {
        householdId,
        presentationId: singlePresentationId,
      });
    }
    return navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS, {
      householdId,
    });
  };
  const deliverables: DeliverablesCardProps = {
    profile,
    suggestionCount,
    presentationCount,
    onReviewSuggestions: canEdit ? onReviewSuggestions : undefined,
    presentation:
      running && presentation === 'not-created' ? undefined : presentation,
    onViewPresentations: viewPresentations,
    onCreatePresentation: canEdit
      ? () => setCreatePresentation(true)
      : undefined,
  };
  if (complete && !showCompletion) return null;
  const showHeader =
    !showProcessing && (presentationFailed || (complete && showCompletion));
  return (
    <>
      <Stack
        spacing={3}
        useFlexGap
        sx={{
          pt: showProcessing || showHeader ? 0 : { xs: 2, md: 3 },
          // The following card section adds 24px of top padding: 8 + 24 = 32px.
          pb:
            showProcessing || (showHeader && hasPresentation)
              ? 1
              : { xs: 2, md: 3 },
          '@keyframes setupFadeIn': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          animation: complete ? 'none' : 'setupFadeIn 600ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {showHeader && (
          <CompletionHero
            hasPresentation={hasPresentation}
            presentationFailed={presentationFailed}
            presentationCount={presentationCount}
            onDismiss={onDismissCompletion}
            onViewPresentations={viewPresentations}
            suggestionCount={suggestionCount}
            onReviewSuggestions={canEdit ? onReviewSuggestions : undefined}
          />
        )}
        {showProcessing && (
          <ProcessingPanel
            householdName={householdName}
            progress={progress}
            running={running}
            failed={failed}
            deliverables={deliverables}
          />
        )}
      </Stack>
      {createPresentation && (
        <PresentationTemplateSelectionModal
          householdId={householdId}
          onClose={() => setCreatePresentation(false)}
        />
      )}
    </>
  );
}
